import React, { useState, useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Select, { components } from 'react-select';
import { notify } from 'react-notify-toast';
import {
  projectLoadRoles,
  updateRole as storeUpdateRole,
  createRole as storeCreateRole,
  projectDeleteRole as storeProjectDeleteRole,
} from '@united-talent-agency/julius-frontend-store';
import { AddButton } from '@united-talent-agency/julius-frontend-components';

import { icons } from '../../../../../styles/icons';
import { AuspiceAdder } from './auspice-adder';
import { GroupByAuspicesTable } from './groupby-auspice-table';
import { AuspicesTable } from './auspices-table';
import Checkbox from '../../../../../components/checkbox/reactstrap-free-checkbox';
import cypressTags from '../../../../../support/cypressTags';
import { getAlgoliaKeys } from '../../../../../support/algolia/algolia-keys-service';

const determineLabel = term => {
  switch (term) {
    case 'type':
      return 'Auspice';
    case 'status':
      return 'Role Status';
    case 'person':
      return 'Person';
    case 'agency':
      return 'Agency';
    default:
      throw Error('UNSUPPORTED GROUP BY LABEL PRESENTED');
  }
};

const possibleGroupByTerms = ['type', 'status', 'person', 'agency'].map(term => ({
  value: term,
  label: determineLabel(term),
}));

const groupBySelectStyles = {
  container: provided => ({
    ...provided,
    marginRight: '3px',
    minWidth: '100px',
    cursor: 'pointer',
    '&:hover': {
      border: 'none',
    },
  }),
  control: provided => ({
    ...provided,
    cursor: 'pointer',
    boxShadow: 'none',
    border: 'none',
    backgroundColor: '',
    color: '',
    '&:hover': {
      border: 'none',
    },
  }),
  option: provided => ({
    ...provided,
    cursor: 'pointer',
    minWidth: '144px',
  }),
  menuList: provided => ({
    ...provided,
    minWidth: '144px',
    fontSize: '14px',
  }),
  menu: provided => ({
    ...provided,
    minWidth: '144px',
  }),
  indicatorsContainer: provided => ({
    ...provided,
    display: 'none',
  }),
  placeholder: provided => ({
    ...provided,
    color: 'black',
  }),
};

const CustomOption = props => {
  const { data, selectProps } = props;
  const display =
    selectProps.value === data.value ? (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {data.label}
        <i className="fa fa-check" aria-hidden="true" />
      </div>
    ) : (
      data.label
    );
  return <components.Option {...props}>{display}</components.Option>;
};

const CustomControl = ({ children, ...props }) => {
  return (
    <components.Control {...props}>
      <span>
        <i
          aria-hidden="true"
          style={{ ...icons.groupByIcon, ...{ height: '14px', width: '14px', marginRight: '3px', marginLeft: '15px' } }}
        />
        GROUP
      </span>
      {children}
    </components.Control>
  );
};

const AuspiceSettings = ({ showArchivedPositions, setShowArchivedPositions, setGroupByTerm, groupByTerm, groupByTermOptions, title = 'Auspices' }) => {
  return (
    <span className="positions-tab-settings-container">
      <Select
        onChange={({ value }) => {
          value === groupByTerm ? setGroupByTerm(null) : setGroupByTerm(value);
        }}
        options={groupByTermOptions || possibleGroupByTerms}
        placeholder=""
        value={groupByTerm}
        styles={groupBySelectStyles}
        controlShouldRenderValue={false}
        isSearchable={false}
        components={{ Control: CustomControl, Option: CustomOption }}
      />
      <Checkbox
        checked={showArchivedPositions}
        onChange={() => setShowArchivedPositions(!showArchivedPositions)}
        title={`Show Archived ${title}`}
        extraStyles={{ cursor: 'pointer' }}
      />
    </span>
  );
};

export const AuspicesView = props => {
  const dispatch = useDispatch();
  const [algoliaKeys, setAlgoliaKeys] = useState();
  const keyCheckIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const algoliaKeysRef = useRef(null);

  const { roles, project, roleTabFilter, roleTabValue, positions, onPersonSelected, title = 'Auspices' } = props;
  const positionLabel = title === 'Production Arts' ? 'PA Vocation' : 'Auspice';
  if (!project?._id && roles?.length === 0) {
    notify.show('Unable to load content, Please try later!', 'custom', 2000, {
      background: '#000000',
      text: '#FFFFFF',
    });
  }

  const refreshAlgoliaKeys = async () => {
    try {
      const keys = await getAlgoliaKeys();
      // Check if component is still mounted before setting state
      if (!isMountedRef.current) return;
      if (keys && keys.appId && keys.searchApiKey) {
        algoliaKeysRef.current = keys;
        setAlgoliaKeys(keys);
      } else {
        console.warn('Received invalid Algolia keys. Keeping previous keys.');
        // Ensure algoliaKeys is set to empty object if keys are invalid
        if (!algoliaKeysRef.current || !algoliaKeysRef.current.appId) {
          algoliaKeysRef.current = {};
          setAlgoliaKeys({});
        }
      }
    } catch (error) {
      console.error('Error fetching Algolia keys:', error);
      // Check if component is still mounted before setting state
      if (!isMountedRef.current) return;
      // Set algoliaKeys to empty object on error so component can still render
      // Child components should handle empty/invalid keys gracefully
      algoliaKeysRef.current = {};
      setAlgoliaKeys({});
    }
  };

  const checkForKeyUpdates = () => {
    // Check if keys in localStorage have changed (for same-tab updates)
    // Storage events don't fire for same-tab changes, so we need to poll
    if (!isMountedRef.current) return;
    
    try {
      const storedKeysStr = localStorage.getItem('algoliaKeys');
      if (!storedKeysStr) return;
      
      const storedKeys = JSON.parse(storedKeysStr);
      const currentKeys = algoliaKeysRef.current;
      
      // Compare keys to detect changes (check appId and searchApiKey)
      if (storedKeys?.appId && storedKeys?.searchApiKey) {
        if (!currentKeys?.appId || 
            currentKeys.appId !== storedKeys.appId || 
            currentKeys.searchApiKey !== storedKeys.searchApiKey) {
          // Keys have changed, refresh component state
          refreshAlgoliaKeys();
        }
      }
    } catch (error) {
      // Ignore JSON parse errors or other localStorage issues
    }
  };

  const handleStorage = (e) => {
    // Listen for Algolia keys updates from other tabs (storage events only fire for cross-tab changes)
    if (e.key === 'algoliaKeys' && isMountedRef.current) {
      refreshAlgoliaKeys();
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    // Centralized Algolia keys service handles refresh scheduling + retries.
    refreshAlgoliaKeys();
    // Listen for storage events to detect when keys are refreshed by other tabs
    window.addEventListener('storage', handleStorage);
    // Check for same-tab key updates periodically (every 30 seconds)
    // This ensures components stay in sync with manager's proactive refreshes
    keyCheckIntervalRef.current = setInterval(() => checkForKeyUpdates(), 30 * 1000);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('storage', handleStorage);
      if (keyCheckIntervalRef.current) {
        clearInterval(keyCheckIntervalRef.current);
        keyCheckIntervalRef.current = null;
      }
    };
  }, []);

  const [roleState, setRoleState] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loadedRoles: [],
    allRoles: [],
  });
  const [showArchivedPositions, setShowArchivedPositions] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [groupByTerm, setGroupByTerm] = useState(null);

  const createRole = async role => await dispatch(storeCreateRole({ ...role, roleTab: roleTabValue }));

  const filterByRoleTab = (role) => {
    if (!roleTabFilter || roleTabFilter === 'auspice') {
      return !role.roleTab || role.roleTab === 'auspice';
    }
    return role.roleTab === roleTabFilter;
  };

  const loadRoles = async () => {
    await dispatch(projectLoadRoles(project._id)).then(({ body }) => {
      if (body) {
        const tabRoles = body.filter(filterByRoleTab);
        if (showArchivedPositions) {
          setRoleState({ loadedRoles: tabRoles, allRoles: tabRoles });
        } else {
          setRoleState({ loadedRoles: tabRoles.filter(role => role.active), allRoles: tabRoles });
        }
      }
    });
  };

  const updateRole = async (roleId, role) => await dispatch(storeUpdateRole(roleId, role));

  const projectDeleteRole = async roleId => await dispatch(storeProjectDeleteRole(roleId));

  const toggleAdd = () => {
    setIsAddOpen(prevState => !prevState);
  };

  useEffect(() => {
    const tabRoles = (roles || []).filter(filterByRoleTab);
    showArchivedPositions
      ? setRoleState({ loadedRoles: tabRoles, allRoles: tabRoles })
      : setRoleState({ loadedRoles: tabRoles.filter(role => role.active), allRoles: tabRoles });
  }, [roles, showArchivedPositions, roleTabFilter]);

  const groupByTermOptions = [
    { value: 'type', label: positionLabel },
    { value: 'status', label: 'Role Status' },
    { value: 'person', label: 'Person' },
    { value: 'agency', label: 'Agency' },
  ];

  return (
    <React.Fragment>
      <div className="auspices-header" data-cy={cypressTags.PROJECT.ADD_AUSPICE_BUTTON}>
        <AddButton label={positionLabel} isOpen={isAddOpen} toggleAdd={toggleAdd} />
        <AuspiceSettings
          showArchivedPositions={showArchivedPositions}
          setShowArchivedPositions={setShowArchivedPositions}
          groupByTerm={groupByTerm}
          setGroupByTerm={setGroupByTerm}
          groupByTermOptions={groupByTermOptions}
          title={title}
        />
      </div>
      {isAddOpen && (
        <AuspiceAdder
          loadRoles={loadRoles}
          project={project}
          createRole={createRole}
          allRoles={roleState.allRoles}
          onDone={toggleAdd}
          algoliaKeys={algoliaKeys}
          positions={positions}
          onPersonSelected={onPersonSelected}
          positionLabel={positionLabel}
        />
      )}

      {groupByTerm === null ? (
        <AuspicesTable
          roles={roleState.loadedRoles}
          loadRoles={loadRoles}
          project={project}
          updateRole={updateRole}
          projectDeleteRole={projectDeleteRole}
          allRoles={roleState.allRoles}
          algoliaKeys={algoliaKeys}
          positions={positions}
          positionLabel={positionLabel}
        />
      ) : (
        <GroupByAuspicesTable
          roles={roleState.loadedRoles}
          loadRoles={loadRoles}
          project={project}
          updateRole={updateRole}
          projectDeleteRole={projectDeleteRole}
          allRoles={roleState.allRoles}
          groupByTerm={groupByTerm}
          positions={positions}
          positionLabel={positionLabel}
        />
      )}
    </React.Fragment>
  );
};
